// components/NewsContent.jsx - Composant affichage des actualités (TOUTES CORRECTIONS APPLIQUÉES)
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  AlertCircle, 
  ExternalLink, 
  Clock, 
  Eye, 
  Bookmark, 
  Star, 
  ChevronRight,
  Grid3X3,
  List,
  Image,
  TrendingUp,
  Globe
} from 'lucide-react';

const NewsContent = ({
  filteredNews,
  loading,
  error,
  searchQuery,
  selectedTheme,
  viewMode,
  themes,
  objectiveId,
  objectiveTheme,
  semanticAnalysisInfo, // 🔧 AJOUT : Informations d'analyse sémantique
  getNewsThemeFromObjective,
  onReadNews,
  onThemeSelect,
  onShowAdmin,
  onClearSearch,
  onViewModeChange,
  showSearchInHeader = true // 🔧 AJOUT : Pour savoir si la recherche est dans l'header
}) => {
  // ================================
  // FONCTIONS UTILITAIRES
  // ================================

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'À l\'instant';
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `Il y a ${diffDays}j`;
    const diffMonths = Math.floor(diffDays / 30);
    return `Il y a ${diffMonths} mois`;
  };

  const getFullDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSelectedThemeLabel = () => {
    if (selectedTheme === 'all') return 'Toutes les actualités récentes';
    const theme = themes.find(t => t.id === selectedTheme);
    return theme ? theme.label : 'Actualités';
  };

  // 🔧 CORRECTION 2 : Fonction pour gérer l'ouverture des articles
  const handleArticleClick = (article, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🔗 Clic sur article:', article.title);
    
    // 🔧 CORRECTION 2 : Priorité à l'ouverture directe dans le navigateur
    if (article.url && article.url !== '#') {
      console.log('🌐 Ouverture URL:', article.url);
      window.open(article.url, '_blank', 'noopener,noreferrer');
    } else if (onReadNews && typeof onReadNews === 'function') {
      console.log('📰 Utilisation onReadNews');
      onReadNews(article);
    } else {
      console.warn('⚠️ Aucune URL ou fonction onReadNews disponible');
    }
  };

  // ================================
  // COMPOSANT CARTE D'ACTUALITÉ - CORRECTION 2 & 3: Liens fonctionnels et images améliorées
  // ================================

  const NewsCard = ({ article }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    
    const handleImageError = () => {
      setImageError(true);
      setImageLoading(false);
    };

    const handleImageLoad = () => {
      setImageLoading(false);
    };
    
    // CORRECTION 3: URLs d'images améliorées - Priorité aux thumbnails pour format compact
    const getImageSrc = () => {
      // 1. Priorité : Thumbnail (mieux adapté au format h-28)
      if (!imageError && article.images) {
        // Essayer le thumbnail en priorité (mieux adapté au format compact)
        if (article.images.thumbnail?.url) {
          return article.images.thumbnail.url;
        }
        // Essayer le fallback (favicon haute résolution)
        if (article.images.fallback?.url) {
          return article.images.fallback.url;
        }
        // En dernier recours, l'image principale
        if (article.images.main?.url) {
          return article.images.main.url;
        }
      }
      
      // 2. Fallback : imageUrl classique (Perplexity ou extraction manuelle)
      if (!imageError && article.imageUrl) {
        return article.imageUrl;
      }
      
      // 3. Fallback final : Images Unsplash thématiques
      const fallbackImages = [
        `https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=240&fit=crop&auto=format`,
        `https://images.unsplash.com/photo-1585776245991-cf89dd7fc73a?w=400&h=240&fit=crop&auto=format`,
        `https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=240&fit=crop&auto=format`,
        `https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400&h=240&fit=crop&auto=format`,
        `https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=240&fit=crop&auto=format`
      ];
      
      // Rotation des images de fallback basée sur l'index de l'article
      const index = Math.abs(article.title.length) % fallbackImages.length;
      return fallbackImages[index];
    };

    const isVeryRecent = () => {
      const publishDate = new Date(article.publishedAt);
      const now = new Date();
      const diffHours = (now - publishDate) / (1000 * 60 * 60);
      return diffHours < 48;
    };

    const isObjectiveRelated = objectiveId && article.theme === getNewsThemeFromObjective(objectiveTheme);
    const themeInfo = themes.find(t => t.id === article.theme);

    // 🔧 CORRECTION 2 : Gestion des scores sémantiques
    const getSemanticScoreColor = (score) => {
      if (score >= 8) return 'bg-green-100 text-green-700';
      if (score >= 6) return 'bg-blue-100 text-blue-700';
      if (score >= 4) return 'bg-yellow-100 text-yellow-700';
      return 'bg-red-100 text-red-700';
    };

    return (
      <div 
        className={`bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer ${
          isObjectiveRelated ? 'border-yellow-300 ring-1 ring-yellow-400 ring-opacity-50' : 'border-gray-200'
        }`}
        onClick={(e) => handleArticleClick(article, e)} // 🔧 CORRECTION 2 : Clic sur toute la carte
      >
        {/* Image avec gestion d'erreur améliorée */}
        <div className="relative h-28 overflow-hidden bg-gray-100">
          {imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
              <div className="animate-pulse flex items-center gap-2 text-gray-400">
                <Image className="h-6 w-6" />
                <span className="text-sm">Chargement...</span>
              </div>
            </div>
          )}
          
          <img 
            src={getImageSrc()}
            alt={article.title}
            className={`w-full h-full object-cover transition-all duration-300 hover:scale-105 ${
              imageLoading ? 'opacity-0' : 'opacity-100'
            }`}
            loading="lazy"
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
          
          {/* Badge thème */}
          <div className="absolute top-3 left-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${
              themeInfo?.color || 'bg-gray-500'
            }`}>
              {themeInfo?.icon} {themeInfo?.label}
            </span>
          </div>

          {/* 🔧 CORRECTION 2 : Badge score sémantique IA */}
          {article.semanticScore && semanticAnalysisInfo && (
            <div className="absolute top-3 right-3">
              <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getSemanticScoreColor(article.semanticScore)}`}>
                Score : {article.semanticScore.toFixed(1)}
              </span>
            </div>
          )}

          {/* Badge objectif */}
          {isObjectiveRelated && (
            <div className="absolute bottom-3 right-3">
              <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                <Star className="h-3 w-3" />
                Objectif
              </span>
            </div>
          )}

        </div>
        
        {/* Contenu */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 leading-tight hover:text-blue-600 transition-colors main-text">
            {article.title}
          </h3>
          
          {article.description && (
            <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-3">
              {article.description}
            </p>
          )}
          
          {/* Métadonnées */}
          <div className="space-y-2 mb-4">
            {/* Ligne 1 : Source et Date/Heure complète */}
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Globe className="h-3 w-3" />
              <span className="font-medium truncate max-w-32">{article.source}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {getFullDateTime(article.publishedDate || article.publishedAt)}
              </span>
            </div>
            
            {/* Ligne 2 : Provider et métriques */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                {article.provider && (
                  <span className={`px-2 py-0.5 rounded-full font-medium ${
                    article.provider === 'Google' 
                      ? 'bg-blue-100 text-blue-700' 
                      : article.provider === 'Perplexity'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {article.provider}
                  </span>
                )}
              </div>
              
              {/* Métriques d'engagement */}
              <div className="flex items-center gap-2">
                {article.totalViews > 0 && (
                  <div className="flex items-center gap-1 text-blue-600">
                    <Eye className="w-3 h-3" />
                    <span>{article.totalViews}</span>
                  </div>
                )}              
              </div>
            </div>
          </div>
          
          {/* Barre de pertinence/qualité améliorée */}
          {(article.qualityScore || article.semanticScore) && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                <span>
                  {(article.semanticScore || semanticAnalysisInfo || objectiveId) ? 'Pertinence' : 'Qualité'}
                </span>
                <span>
                  {article.semanticScore ? `${article.semanticScore.toFixed(1)}/10` : `${article.qualityScore}/10`}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    article.semanticScore 
                      ? article.semanticScore >= 7 ? 'bg-green-500' : article.semanticScore >= 5 ? 'bg-blue-500' : 'bg-yellow-500'
                      : 'bg-blue-500'
                  }`}
                  style={{ 
                    width: `${((article.semanticScore || article.qualityScore) / 10) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          )}
          
          {/* Indicateur de clicabilité */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 text-blue-600 text-xs font-medium">
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Cliquez pour lire</span>
            </div>
            
            <button 
              className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              onClick={(e) => {
                e.stopPropagation(); // Empêcher la propagation pour le bookmark
                // Logique de bookmark ici
              }}
              title="Sauvegarder"
            >
              <Bookmark className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ================================
  // ÉTATS DE CHARGEMENT ET D'ERREUR
  // ================================

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="bg-white rounded-lg shadow-sm animate-pulse border border-gray-200">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="section-title text-gray-900 mb-2">
          Erreur de chargement
        </h3>
        <p className="text-gray-600 mb-6 main-text">{error}</p>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors button-text"
          >
            Réessayer
          </button>
          <button
            onClick={onShowAdmin}
            className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors button-text"
          >
            Gérer les actualités
          </button>
        </div>
      </div>
    );
  }

  // ================================
  // RENDU PRINCIPAL
  // ================================

  const gridClass = viewMode === 'grid' 
    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
    : 'space-y-4';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* En-tête de section avec contrôles d'affichage - CORRECTION 2 */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          
          <div className="flex items-center gap-4">
            {/* Sélecteur de mode d'affichage */}
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => onViewModeChange('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Vue en grille"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onViewModeChange('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Vue en liste"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            
            {(selectedTheme !== 'all' && !searchQuery && !objectiveId) && (
              <button
                onClick={() => onThemeSelect('all')}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 button-text"
              >
                Voir toutes les actualités
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Contenu des actualités */}
      <div className="p-6">
        <div className={gridClass}>
          {filteredNews.map((article, index) => (
            <NewsCard key={article.id || `${article.title}-${index}`} article={article} />
          ))}
        </div>
      </div>

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .5; }
        }

        .hover\\:scale-105:hover {
          transform: scale(1.05);
        }

        .hover\\:-translate-y-1:hover {
          transform: translateY(-0.25rem);
        }

        .transition-all {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .ring-1 {
          --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
          --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
          box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
        }

        .ring-yellow-400 { --tw-ring-color: rgb(250 204 21); }
        .ring-opacity-50 { --tw-ring-opacity: 0.5; }

        .shadow-sm {
          --tw-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
        }

        .shadow-lg {
          --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
          box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
        }

        .max-w-24 {
          max-width: 6rem;
        }

        .truncate {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
};

export default NewsContent;
